import type {
  RemovedSection,
  RemovedSubsection,
} from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { DisclosureHeader, useDisclosure } from "@/components/ui/Disclosure";
import { EmptyState } from "@/components/ui/EmptyState";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { PlaceIn } from "./PlaceIn";
import { EXAMPLES_WORDS, definitionsWords, type Labels } from "./SubsectionRow";

export type RemovedItem = RemovedSection | RemovedSubsection;

function described(
  item: RemovedItem,
  labels: Labels,
): { kind: string; words: string } {
  if (item.kind === "section")
    return { kind: "Section", words: item.section.title };
  const subsection = item.subsection;
  switch (subsection.kind) {
    case "figure":
      return {
        kind: "Figure",
        words: `FIG. ${labels.figure(subsection.file_id) ?? "?"}`,
      };
    case "claim": {
      const number = labels.claim(subsection.claim_id);
      return {
        kind: "Claim",
        words: number === undefined ? "Claim" : `Claim ${String(number)}`,
      };
    }
    case "text":
      return { kind: "Text line", words: subsection.text };
    case "examples":
      return { kind: "Examples", words: EXAMPLES_WORDS };
    case "definitions":
      return { kind: "Definitions", words: definitionsWords(subsection) };
  }
}

/** The act that brings a removed item back: to where it was, or, its section gone, to one chosen. */
function BringBack({
  item,
  words,
  needsTarget,
  sections,
  onReinstate,
  onPlace,
}: {
  item: RemovedItem;
  words: string;
  needsTarget: ReadonlySet<string>;
  sections: { id: string; title: string }[];
  onReinstate: (item: RemovedItem) => void;
  onPlace: (item: RemovedSubsection, sectionId: string) => void;
}) {
  if (
    item.kind === "subsection" &&
    needsTarget.has(item.subsection.subsection_id)
  ) {
    return (
      <PlaceIn
        words={words}
        sections={sections}
        onPlace={(sectionId) => {
          onPlace(item, sectionId);
        }}
      />
    );
  }
  return (
    <Button
      variant="secondary"
      size="dense"
      onClick={() => {
        onReinstate(item);
      }}
    >
      Reinstate
    </Button>
  );
}

/**
 * What Praxis proposed and the attorney deleted, each with the act that puts it back where it
 * was. A line whose section is gone is placed in a section the attorney chooses instead.
 * Folded by default: it is a record, not the plan.
 */
export function RemovedFold({
  removed,
  labels,
  sectionTitle,
  readOnly,
  needsTarget,
  sections,
  onReinstate,
  onPlace,
}: {
  removed: RemovedItem[];
  labels: Labels;
  sectionTitle: (sectionId: string) => string | undefined;
  readOnly: boolean;
  /** The removed lines whose section is gone, which the server names. */
  needsTarget: ReadonlySet<string>;
  sections: { id: string; title: string }[];
  onReinstate: (item: RemovedItem) => void;
  onPlace: (item: RemovedSubsection, sectionId: string) => void;
}) {
  const fold = useDisclosure(false);
  return (
    <EmptyState shape="card" className="mt-6 text-left">
      <DisclosureHeader
        trigger={fold.trigger}
        open={fold.open}
        title={
          <span className="font-semibold">{`Removed · ${String(removed.length)}`}</span>
        }
        meta={
          <span className="text-xs text-muted-foreground">
            Items Patent Studio generated that you deleted. Reinstate any of
            them up to generation.
          </span>
        }
      />
      <ul {...fold.panel} className="mt-3 space-y-2">
        {removed.map((item) => {
          const { kind, words } = described(item, labels);
          const from =
            item.kind === "subsection"
              ? sectionTitle(item.from_section_id)
              : undefined;
          const key =
            item.kind === "section"
              ? item.section.section_id
              : item.subsection.subsection_id;
          return (
            <li key={key} className="flex items-center gap-3 text-sm">
              <MicroLabel>{kind}</MicroLabel>
              <span className="min-w-0 flex-1 truncate">
                {words}
                {from !== undefined && (
                  <span className="text-muted-foreground">{` · from ${from}`}</span>
                )}
              </span>
              {!readOnly && (
                <BringBack
                  item={item}
                  words={words}
                  needsTarget={needsTarget}
                  sections={sections}
                  onReinstate={onReinstate}
                  onPlace={onPlace}
                />
              )}
            </li>
          );
        })}
      </ul>
    </EmptyState>
  );
}
